import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import Datastore from "../../communication/Datastore";
import Utilities from "../../foundation/Utility";
import { REHYDRATE } from "redux-persist";
import store from "../../state/Store";
import IntegrationLogEntry from "../../types/system/IntegrationLogEntry";
import PageSpecification from "../../types/system/PageSpecification";

// Define the type for our state store entry
type SifLogState = {
    allData: Record<number, Record<number, IntegrationLogEntry[]>>,
    pageData: IntegrationLogEntry[],
    virtualSpec: SifLogPageSpecification,
    internalSpec: Record<number, SifLogPageSpecification>,
    status: string,
    error: string
}

// Define the initial state for our state store entry
const initialState: SifLogState = {
    allData: {},
    pageData: [],
    virtualSpec: {
        page: 0,
        size: 20,
        total: 0
    } as SifLogPageSpecification,
    internalSpec: {},
    status: "idle",
    error: null
}

// Create a slice to hold all our reducers
const sifLogSlice = createSlice({
    name: "sifLog",
    initialState: initialState,
    reducers: {
        setVirtualSpecType: (state, action: PayloadAction<number>) => {
            state.virtualSpec.parent = action.payload;
        },
        addSifInternalSpecification: (state, action: PayloadAction<SifLogPageSpecification>) => {
            if (!state.internalSpec[action.payload.parent])
                state.internalSpec[action.payload.parent] = {
                    parent: action.payload.parent,
                    page: -1,
                    size: 200,
                    total: 0
                } as SifLogPageSpecification;
        },
        setVirtualPageAndSize: (state, action: PayloadAction<SifLogPageSpecification>) => {
            state.virtualSpec.parent = action.payload.parent;
            state.virtualSpec.page = action.payload.page;
            state.virtualSpec.size = action.payload.size;
            state.virtualSpec.total = state.internalSpec[action.payload.parent].total;
            
            const internalPageIndex = Math.floor(state.virtualSpec.page * state.virtualSpec.size / state.internalSpec[action.payload.parent].size);
            const internalPage = state.allData[action.payload.parent][internalPageIndex];
            const translatedPageStart = state.virtualSpec.page * state.virtualSpec.size - internalPageIndex * state.internalSpec[action.payload.parent].size;
            const translatedPageEnd = translatedPageStart + state.virtualSpec.size;

            state.pageData = internalPage?.slice(Math.min(translatedPageStart, internalPage.length), Math.min(translatedPageEnd, internalPage.length)) ?? [];
            
            state.virtualSpec.waiting = state.virtualSpec.loading && state.pageData.length === 0;

            while (state.pageData.length < state.virtualSpec.size)
                state.pageData.push(new IntegrationLogEntry());
        }
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;

                const allData = {};

                if (rawData?.payload?.sifLogs?.allData)
                    for (let entry in rawData?.payload?.sifLogs.allData) {
                        allData[entry] = {};

                        for (let entrySet in rawData?.payload?.sifLogs.allData[entry])
                            allData[entry][entrySet] = rawData?.payload?.sifLogs.allData[entry][entrySet].map(item => new IntegrationLogEntry(item));
                    }
                    
                return {
                    allData: allData,
                    pageData: rawData?.payload?.sifLogs?.pageData?.map(item => new IntegrationLogEntry(item)) ?? [],
                    virtualSpec: rawData?.payload?.sifLogs?.virtualSpec ?? {
                        page: 0,
                        size: 20,
                        total: 0
                    } as PageSpecification,
                    internalSpec: rawData?.payload?.sifLogs?.internalSpec ?? {},
                    status: "idle",
                    error: null
                }
            })
            .addCase(fetchLogPage.pending, (state, action) => {
                state.status = "loading"
                state.virtualSpec.loading = true;
                Object.keys(state.internalSpec).forEach(key =>
                    state.internalSpec[key].loading = true);
            })
            .addCase(fetchLogPage.fulfilled, (state, action) => {
                state.status = "succeeded"
                // Add any fetched posts to the array
                state.virtualSpec.total = action.payload[2];
                state.internalSpec[action.payload[0]].total = action.payload[2];
                state.virtualSpec.loading = false;
                Object.keys(state.internalSpec).forEach(key =>
                    state.internalSpec[key].loading = true);
                state.allData[action.payload[0]] = state.allData[action.payload[0]] ?? {};
                state.allData[action.payload[0]][action.payload[1]] = action.payload[3].map(item => new IntegrationLogEntry(item));

                const internalPageIndex = Math.floor(state.virtualSpec.page * state.virtualSpec.size / state.internalSpec[action.payload[0]].size);
                const internalPage = state.allData[action.payload[0]][internalPageIndex];
                const translatedPageStart = state.virtualSpec.page * state.virtualSpec.size - internalPageIndex * state.internalSpec[action.payload[0]].size;
                const translatedPageEnd = translatedPageStart + state.virtualSpec.size;

                state.pageData = internalPage?.slice(Math.min(translatedPageStart, internalPage.length), Math.min(translatedPageEnd, internalPage.length)) ?? [];

                state.virtualSpec.waiting = state.virtualSpec.loading && state.pageData.length === 0;

                while (state.pageData.length < state.virtualSpec.size)
                    state.pageData.push(new IntegrationLogEntry());
            })
            .addCase(fetchLogPage.rejected, (state, action) => {
                state.status = "failed"
                state.virtualSpec.loading = false;
                Object.keys(state.internalSpec).forEach(key =>
                    state.internalSpec[key].loading = false);
                state.error = action.error.message
            })
    }
})

const {
    setVirtualSpecType,
    setVirtualPageAndSize
} = sifLogSlice.actions;

export const {
    addSifInternalSpecification
} = sifLogSlice.actions;

interface LoadArgs {
    parent: number,
    fetchIndex: number
}
export class SifLogPageSpecification implements PageSpecification {
    page: number;
    size: number;
    total: number;
    loading: boolean;
    waiting: boolean;
    parent: number;
}

// This is a thunk, they are used to load the state
const fetchLogPage = createAsyncThunk("sifLog/fetchLogPage", async (loadArgs: LoadArgs, thunkAPI) => {
    const { sifLogs } = store.getState().db;
    const internalSpec = sifLogs.internalSpec[loadArgs.parent];
    const response = await  Promise.all([
        Datastore.getType<number>(`api/SIF/Consumptions/${loadArgs.parent}/ErrorLog/Count`),
        Datastore.getType<IntegrationLogEntry[]>(`api/SIF/Consumptions/${loadArgs.parent}/ErrorLog/Page/${loadArgs.fetchIndex}/Size/${internalSpec.size}`)
    ]);
    const result = [
        loadArgs.parent,
        loadArgs.fetchIndex,
        response[0],
        response[1]
    ] as [number, number, number, IntegrationLogEntry[]];
    return result;
});
const evaluatePrivateData = createAsyncThunk("sifLog/evaluatePrivateData", async (specification: SifLogPageSpecification, thunkAPI): Promise<void> => {
    const { sifLogs } = store.getState().db;
    const internalSpec = sifLogs.internalSpec[specification.parent];

    const requiredInternalPage = Math.max(Math.ceil(((specification.page + 1) * specification.size - 1) / internalSpec.size) - 1, 0);

    if (sifLogs.status !== "loading") {
        if (!sifLogs.allData[specification.parent] ||
            !sifLogs.allData[specification.parent][requiredInternalPage]) {
            await thunkAPI.dispatch(fetchLogPage({ parent: specification.parent, fetchIndex: requiredInternalPage } as LoadArgs));
        } else {
            const direction = (
                specification.page > sifLogs.virtualSpec.page ? 1 :
                specification.page < sifLogs.virtualSpec.page ? -1 :
                0
            );

            if (direction !== 0) {
                const pageGap = direction > 0 ?
                    (internalSpec.size - (specification.page * specification.size - requiredInternalPage * internalSpec.size)) / specification.size :
                    (specification.page * specification.size - requiredInternalPage * internalSpec.size) / specification.size;

                if (pageGap < 4) {
                    if (direction < 0 && requiredInternalPage > 0 && !sifLogs.allData[requiredInternalPage - 1]) {
                        await thunkAPI.dispatch(fetchLogPage({ parent: specification.parent, fetchIndex: requiredInternalPage - 1 }));
                    } else if (direction > 0 && !sifLogs.allData[requiredInternalPage + 1]) {
                        await thunkAPI.dispatch(fetchLogPage({ parent: specification.parent, fetchIndex: requiredInternalPage + 1 }));
                    }
                }
            }
        }
    }
});
export const getSifLogPage = createAsyncThunk("sifLog/getSifLogPage", async (parent: number, thunkAPI): Promise<void> => {
    thunkAPI.dispatch(setVirtualSpecType(parent));
    const { sifLogs } = store.getState().db;
    thunkAPI.dispatch(evaluatePrivateData(sifLogs.virtualSpec));
});
export const setSifLogPageAndSize = createAsyncThunk("sifLog/setSifLogPageAndSize", async (specification: SifLogPageSpecification, thunkAPI) => {
    thunkAPI.dispatch(evaluatePrivateData(specification));
    thunkAPI.dispatch(setVirtualPageAndSize(specification));
});
export const changeSifLogParent = createAsyncThunk("sifLog/changeSifLogParent", async (parent: number, thunkAPI) => {
    const { sifLogs } = store.getState().db;
    const workingSpec = Utilities.clone(sifLogs.virtualSpec, { parent : parent });

    thunkAPI.dispatch(addSifInternalSpecification(workingSpec));
    await thunkAPI.dispatch(evaluatePrivateData(workingSpec));
    thunkAPI.dispatch(setVirtualPageAndSize(workingSpec));
});

// We have to export the reducer as this is added to the state store
export default sifLogSlice.reducer